import {
  ArrowPathIcon,
  PlusCircleIcon,
  XMarkIcon,
  CheckIcon,
  PlusIcon,
} from "@heroicons/react/24/solid";
import {useState, useEffect, useMemo} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import { TextInput, ToggleInput, CheckboxInput } from "../components/Inputs";
import { PageHeader } from "../components/Layouts";
import { ActiveFilter, PrimaryTable } from "../components/Tables";
import { useLoaderStore, useUserStore } from "../context";
import {useAlert, useApi, useTenant} from "../hooks";
import constants from "../utils/constants";
import dateHelpers from "../utils/dateHelpers";
import {formatPhone, switchSortDirection, userHasRole} from "../utils/helpers";
import { PrimaryButton } from "../components/Buttons";
import { Popover } from "@headlessui/react";
import {useActiveTenant} from "../context/useSystemTenantStore";
import useDevManager from "../context/useDevManager";
import {formatPhoneNumber} from "react-phone-number-input";

const EMPTY_FILTERS = {
  activeOnly: false,
};

function getFiltersFromSearchParams(searchParams) {
  return {
    ...EMPTY_FILTERS,
    activeOnly: searchParams.get("activeOnly") === "true",
    name: searchParams.get("name") || undefined,
  };
}

function buildSearchParams({
                             filters,
                             currentPage,
                             displayCount,
                             sortDirection,
                             sortField,
                           }) {
  const params = {};

  if (currentPage && currentPage !== 1) params.pageNumber = String(currentPage);
  if (displayCount) params.pageSize = String(displayCount);
  if (sortDirection) params.sortDirection = sortDirection;
  if (sortField) params.sortField = sortField;

  if (filters.activeOnly) params.activeOnly = "true";
  if (filters.name) params.name = filters.name;

  return params;
}

export default function SystemTenants() {
  const navigate = useNavigate();
  const alert = useAlert();
  const { post, fetch } = useApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setShowLoader } = useLoaderStore();
  const { currentUser } = useUserStore();
  const { IS_DEV } = useDevManager();
  const activeFilters = useMemo(
      () => getFiltersFromSearchParams(searchParams),
      [searchParams]
  );

  const [stagedFilters, setStagedFilters] = useState(() => ({
    ...EMPTY_FILTERS,
    ...getFiltersFromSearchParams(searchParams),
  }));
  const currentPage = Number(searchParams.get("pageNumber")) || 1;
  const sortDirection =
      searchParams.get("sortDirection") || constants.SORT_DIRECTIONS.ASCENDING;
  const sortField = searchParams.get("sortField") || "id";
  const displayCount =
      Number(searchParams.get("pageSize")) || constants.DISPLAY_PER_PAGE || 25;
  const [data, setData] = useState({ list: [], totalCount: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const activeTenant = useActiveTenant();
  const SYSTEM_TENANT_ID = activeTenant?.id;

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, displayCount, activeFilters, sortDirection, sortField]);

  useEffect(() => {
    setStagedFilters(activeFilters);
  }, [activeFilters]);

  const getData = () => {
    setIsLoading(true);
    let payload = {
      pageNumber: currentPage,
      pageSize: displayCount,
      sortDirection,
      sortField,
      isExact: false,
      ...activeFilters,
    };
    if (!userHasRole(currentUser, constants.ROLE_IDS.SYS_ADMIN)) {
      let accountRoles = currentUser.roles?.filter(role => role.accountId);
      payload.accountIds = accountRoles.map(role => role.accountId);
    }
    fetch(`systemTenant/systemTenantList/${SYSTEM_TENANT_ID}`, payload)
      .then((res) => {
        setData(res);
      })
      .catch((err) => {
        alert("Server error", "Could not retrieve systemTenants", "error");
      })
      .finally(() => setIsLoading(false));
  };

  const updateTableParams = (updates) => {
    const nextFilters = updates.filters ?? activeFilters;
    const nextCurrentPage = updates.currentPage ?? currentPage;
    const nextDisplayCount = updates.displayCount ?? displayCount;
    const nextSortDirection = updates.sortDirection ?? sortDirection;
    const nextSortField = updates.sortField ?? sortField;

    setSearchParams(
        buildSearchParams({
          filters: nextFilters,
          currentPage: nextCurrentPage,
          displayCount: nextDisplayCount,
          sortDirection: nextSortDirection,
          sortField: nextSortField,
        })
    );
  };

  const applyFilters = (nextFilters) => {
    setStagedFilters(nextFilters);
    updateTableParams({
      filters: nextFilters,
      currentPage: 1,
    });
  };

  const setCurrentPage = (page) => {
    updateTableParams({ currentPage: page });
  };

  const setDisplayCount = (count) => {
    updateTableParams({
      displayCount: count,
      currentPage: 1,
    });
  };

  const toggleActiveStatus = (systemTenant) => {
      setShowLoader(true);
      post(`systemTenant/toggleSystemTenantActive/${systemTenant.id}`)
        .then((res) => {
          alert("Success",
            res.message || "SystemTenant active status toggled",
            "success");
          getData();
        })
        .catch((err) => {
          alert("Error",
            err.data.message || "SystemTenant active status could not be toggled",
            "error");
          console.error(err);
        })
        .finally(() => {
          setShowLoader(false);
        });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      updateTableParams({
        currentPage: 1,
        sortDirection: switchSortDirection(sortDirection),
      });
    } else {
      updateTableParams({
        currentPage: 1,
        sortField: field,
      });
    }
  };
  // Insert toggleActiveStatus here

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between">
        <PageHeader title="Manage System Tenants" />
        {/* <PlusCircleIcon
          className="w-8 text-primaryColor hover:text-secondaryColor cursor-pointer"
          onClick={() => navigate("/systemTenants/0")}
        /> */}
      </div>

      {/* Filter section */}
      <SystemTenantFilterSection
        stagedFilters={stagedFilters}
        setStagedFilters={setStagedFilters}
        activeFilters={activeFilters}
        setActiveFilters={applyFilters}
      />

      {/* Table */}
      <PrimaryTable
        isLoading={isLoading}
        totalCount={data?.totalCount}
        displayCount={displayCount}
        currentPage={currentPage}
        setDisplayCount={setDisplayCount}
        setCurrentPage={setCurrentPage}
        header={
          <PrimaryTable.Header>
            {IS_DEV &&
            <PrimaryTable.HeaderColumn
              display="Id"
              onSort={() => handleSort("id")}
              isSortingBy={sortField === "id"}
              sortDirection={sortDirection}
            />}
            <PrimaryTable.HeaderColumn
              display="Name"
              onSort={() => handleSort("name")}
              isSortingBy={sortField === "name"}
              sortDirection={sortDirection}
            />
            <PrimaryTable.HeaderColumn
              display="Mobile Phone"
            />
            {/* <PrimaryTable.HeaderColumn display="" /> */}
          </PrimaryTable.Header>
        }
      >
        {data &&
          data.list &&
          data.list.map((x, i) => (
            <PrimaryTable.Row
              key={`${x.id}-${i}`}
              index={i}
              onClick={(e) => {
                if (!x.deactivatedAt) {
                  navigate(`/systemTenants/${x.id}`);
                }
              }}
            >
              {IS_DEV && <PrimaryTable.Cell>{x.id}</PrimaryTable.Cell>}
              <PrimaryTable.Cell>
                {x.name}
              </PrimaryTable.Cell>
              <PrimaryTable.Cell>
                {formatPhone(x.mobilePhone)}
                {/*{x.mobilePhone}*/}
              </PrimaryTable.Cell>
              {/* <PrimaryTable.Cell>
                <div className="flex">
                  {x.deactivatedAt ? (
                      <ArrowPathIcon
                        title="Activate"
                        className="w-6 mx-1 text-white bg-green-500 rounded-md p-0.5 cursor-pointer"
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleActiveStatus(x);
                        }}
                      />
                  ) : (
                      <XMarkIcon
                        title="Deactivate"
                        className="w-6 mx-1 text-white bg-delete rounded-md p-0.5 cursor-pointer"
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleActiveStatus(x);
                        }}
                    />
                  )}
                </div>
              </PrimaryTable.Cell> */}
            </PrimaryTable.Row>
          ))}
      </PrimaryTable>
    </div>
  );
}

const SystemTenantFilterSection = ({
  stagedFilters,
  setStagedFilters,
  activeFilters,
  setActiveFilters,
}) => {
  return (
    <div className="relative mb-4 rounded-xs shadow-sm bg-white p-4">
      <Popover as="div" className="relative">
        {({ open }) => (
          <>
            <Popover.Button className="flex items-center text-sm bg-gray-100 px-3 py-2 rounded-sm hover:bg-gray-200 relative focus:outline-none">
              <PlusIcon className="w-4 mr-1" />
              FILTERS
            </Popover.Button>
            <Popover.Panel
              as="div"
              className="absolute grid gap-y-4 z-50 w-full sm:w-1/3 origin-bottom-right bg-white rounded-md p-4 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none mt-1"
            >
              <div>
                <CheckboxInput
                  name="active-only"
                  label="Show only active system tenants"
                  checked={stagedFilters.activeOnly === true}
                  onChange={() =>
                    setStagedFilters({
                      ...stagedFilters,
                      activeOnly: stagedFilters.activeOnly ? false : true,
                    })
                  }
                />
              </div>
        <div>
          <TextInput
            label="Name"
            placeholder="Enter Name" to filter by
            value={stagedFilters.name}
            onChange={(e) =>
              setStagedFilters({
                ...stagedFilters,
                name: e.target.value || undefined,
              })
            }
          />
        </div>
              {/* Add filters here */}
              <PrimaryButton
                text="Apply filters"
                onClick={() => setActiveFilters(stagedFilters)}
              />
            </Popover.Panel>
          </>
        )}
      </Popover>

      {/* Selected filters */}
      {Object.values(activeFilters).find(
        (val) => val !== undefined && val !== false
      ) ? (
        <div className="flex items-center space-x-2 pt-6 text-sm">
          <div>Selected:</div>
          <ActiveFilter
            display="Show active only"
            value={
              activeFilters.activeOnly ? (
                <CheckIcon className="w-4 text-green-600" />
              ) : undefined
            }
            onClear={() =>
              setActiveFilters({
                ...activeFilters,
                activeOnly: false,
              })
            }
          />

          <ActiveFilter
            display="Name"
            value={activeFilters.name}
            onClear={() =>
              setActiveFilters({
                ...activeFilters,
                name: undefined,
              })
            }
          />
          {/* Add active filters here */}

          <div
            className="cursor-pointer bg-gray-100 px-3 py-2 text-xs hover:bg-gray-200"
            onClick={() => {
              setActiveFilters({ ...EMPTY_FILTERS });
            }}
          >
            Clear all
          </div>
        </div>
      ) : (
        <div className="pt-6 text-sm text-gray-400">No active filters</div>
      )}
    </div>
  );
};
